import { useState, useRef, useEffect } from "react";
import { TextField, InputAdornment, TextFieldProps } from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import { debounce } from "lodash";
import { Position } from "@/types";
import { isFunction, isString } from "@/utils";
import { sxTextField } from "@/theme";

export type DebounceInputProps = Omit<TextFieldProps, "onChange"> & {
  length?: number;
  wait?: number;
  onChange?: (value?: string, name?: string) => void;
  fullWidth?: boolean;
  placeholder?: string;
  searchIcon?: boolean;
  searchIconPosition?: Position;
  clearIcon?: boolean;
  clearIconPosition?: Position;
};

const DebounceInput: React.FC<DebounceInputProps> = ({
  name = undefined,
  length = undefined,
  wait = undefined,
  onChange = undefined,
  fullWidth = false,
  placeholder = undefined,
  searchIcon = false,
  searchIconPosition = "start",
  clearIcon = false,
  clearIconPosition = "end",
  disabled = undefined,
  autoFocus = undefined,
  variant = undefined,
}) => {
  const [value, setValue] = useState<string | undefined>("");

  const delayCall = useRef(
    debounce(
      (
        value?: string,
        name?: string,
        onChange?: (value?: string, name?: string) => void
      ) => {
        if (
          !isNaN(length as number) &&
          isString(value) &&
          value!.length > 0 &&
          value!.length < length!
        ) {
          return;
        }

        if (isFunction(onChange)) {
          onChange?.(value, name);
        }
      },
      wait
    )
  ).current;

  const handleChange = (value?: string) => {
    setValue(value);
    delayCall(value, name, onChange);
  };

  useEffect(() => {
    return () => {
      delayCall.cancel();
    };
  }, [delayCall]);

  return (
    <TextField
      value={value}
      onChange={({ target: { value } }) => handleChange(value)}
      fullWidth={fullWidth}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      variant={variant}
      slotProps={{
        input: {
          ...(searchIcon &&
            !!searchIconPosition && {
              startAdornment:
                searchIconPosition === "start" ? (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ) : null,
              endAdornment:
                searchIconPosition !== "start" ? (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ) : null,
            }),
          ...(clearIcon &&
            !!clearIconPosition && {
              startAdornment:
                clearIconPosition === "start" ? (
                  <InputAdornment position="start">
                    <Clear />
                  </InputAdornment>
                ) : null,
              endAdornment:
                clearIconPosition !== "start" ? (
                  <InputAdornment position="end">
                    <Clear />
                  </InputAdornment>
                ) : null,
            }),
        },
      }}
      sx={sxTextField}
    />
  );
};

export default DebounceInput;
